/**
 * NOTE (Derek, 2026-02-05): useSemanticModifierValue Hook (agent-ui version)
 *
 * Simplified version for agent-ui that only supports semantic element selection.
 * Reads values from the semantic element's computedStyles.
 */
import type {
  ComputedElementStyles,
  SemanticElement,
} from "schemas/generated/semanticElement";

import { useSemanticElement } from "@agent-ui/features/modifiers/store/ModifiersStoreContext";

interface UseSemanticModifierValueOptions<ModifierValue> {
  /**
   * Extractor function for semantic element selection.
   * Reads the value from the semantic element's computedStyles.
   */
  semanticExtractor: (opts: {
    semanticElement: SemanticElement;
    computedStyles: ComputedElementStyles;
  }) => ModifierValue | undefined;
}

/**
 * Hook that returns modifier values from semantic selection.
 */
export function useSemanticModifierValue<ModifierValue>(
  options: UseSemanticModifierValueOptions<ModifierValue>,
): ModifierValue | undefined {
  const { semanticExtractor } = options;

  const semanticElement = useSemanticElement();

  if (semanticElement?.computedStyles) {
    return semanticExtractor({
      semanticElement,
      computedStyles: semanticElement.computedStyles,
    });
  }

  return undefined;
}
